<script>
import { mapGetters, mapActions } from 'vuex';
import { IFrameHelper, RNHelper } from 'widget/helpers/utils';
import { popoutChatWindow } from '../helpers/popoutHelper';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import configMixin from 'widget/mixins/configMixin';
import { CONVERSATION_STATUS } from 'shared/constants/messages';
import Modal from 'dashboard/components/Modal.vue';
import ModalHeader from 'dashboard/components/ModalHeader.vue';

export default {
  name: 'HeaderActions',
  components: {
    FluentIcon,
    Modal,
    ModalHeader,
  },
  mixins: [configMixin],
  props: {
    showPopoutButton: {
      type: Boolean,
      default: false,
    },
    showEndConversationButton: {
      type: Boolean,
      default: true,
    },
    showDeleteButton: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters({
      conversationAttributes: 'conversationAttributes/getConversationParams',
    }),
    canLeaveConversation() {
      return [
        CONVERSATION_STATUS.OPEN,
        CONVERSATION_STATUS.SNOOZED,
        CONVERSATION_STATUS.PENDING,
      ].includes(this.conversationStatus);
    },
    isIframe() {
      return IFrameHelper.isIFrame();
    },
    isRNWebView() {
      return RNHelper.isRNWebView();
    },
    showHeaderActions() {
      return this.isIframe || this.isRNWebView || this.hasWidgetOptions;
    },
    conversationStatus() {
      return this.conversationAttributes.status;
    },
    hasWidgetOptions() {
      return this.showPopoutButton || this.conversationStatus === 'open';
    },
    widgetColor() {
      return this.$store.getters['appConfig/getWidgetColor'];
    },
    isConversationDeletable() {
      return this.conversationStatus === 'resolved';
    },
    isMarkedForDeletion() {
      return this.markedForDeletion;
    },
  },
  data() {
    return {
      showConfirmModal: false,
      markedForDeletion: false,
    };
  },
  methods: {
    ...mapActions('multi-conversation', [
      'deleteConversation',
      'fetchConversations',
    ]),
    popoutWindow() {
      this.closeWindow();
      const {
        location: { origin },
        aiccWebChannel: { websiteToken },
        authToken,
      } = window;
      popoutChatWindow(
        origin,
        websiteToken,
        this.$root.$i18n.locale,
        authToken
      );
    },
    closeWindow() {
      if (IFrameHelper.isIFrame()) {
        IFrameHelper.sendMessage({ event: 'closeWindow' });
      } else if (RNHelper.isRNWebView) {
        RNHelper.sendMessage({ type: 'close-widget' });
      }
    },
    resolveConversation() {
      this.showConfirmModal = true;
    },
    onConfirmResolve() {
      this.$store.dispatch('multi-conversation/resolveConversation');
      this.showConfirmModal = false;
      this.clearDeletionMark();
      this.$router.back();
    },
    onCloseConfirm() {
      this.showConfirmModal = false;
      this.clearDeletionMark();
    },

    // 处理删除会话 - 第一次点击标记，第二次点击删除
    async handleDeleteConversation() {
      // 如果当前会话已经被标记删除，则执行删除
      if (this.markedForDeletion) {
        try {
          const conversationId = this.conversationAttributes.id;
          await this.deleteConversation(conversationId);
          this.clearDeletionMark(); // 删除成功后清除标记

          // 刷新会话列表
          await this.fetchConversations();

          this.$router.push({ name: 'messages' }); // 返回会话列表
        } catch (error) {
          // 删除失败时可以添加用户友好的提示
          // 这里暂时忽略错误，实际应用中可以添加toast通知
        }
      } else {
        // 第一次点击，标记要删除的会话
        this.markedForDeletion = true;
      }
    },

    // 清除删除标记
    clearDeletionMark() {
      this.markedForDeletion = false;
    },

    // 处理全局点击事件
    handleGlobalClick(event) {
      // 检查点击的是否是删除按钮或其子元素
      const deleteButton = event.target.closest('.delete-conversation-button');
      if (!deleteButton && this.markedForDeletion) {
        // 如果点击的不是删除按钮，清除标记
        this.clearDeletionMark();
      }
    },
  },

  mounted() {
    // 添加全局点击监听器
    document.addEventListener('click', this.handleGlobalClick);
  },

  beforeUnmount() {
    // 离开页面时清除标记
    this.clearDeletionMark();
    // 移除全局点击监听器
    document.removeEventListener('click', this.handleGlobalClick);
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="showHeaderActions" class="actions flex items-center gap-3">
    <button
      v-if="
        canLeaveConversation &&
        hasEndConversationEnabled &&
        showEndConversationButton
      "
      class="button transparent compact"
      :title="$t('END_CONVERSATION')"
      @click="resolveConversation"
    >
      <FluentIcon icon="sign-out" size="22" class="text-n-slate-12" />
    </button>

    <!-- 删除按钮（只对已关闭的会话显示） -->
    <button
      v-if="isConversationDeletable && showDeleteButton"
      class="delete-conversation-button p-2 rounded-full transition-all duration-200 transform hover:scale-110 active:scale-95 opacity-70 hover:opacity-100"
      :class="{
        'bg-red-500 hover:bg-red-600': isMarkedForDeletion,
        'hover:bg-red-50 dark:hover:bg-red-900/20': !isMarkedForDeletion,
      }"
      :title="
        isMarkedForDeletion
          ? $t('CONFIRM_DELETE_CONVERSATION')
          : $t('DELETE_CONVERSATION')
      "
      @click="handleDeleteConversation"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        :class="{
          'text-white': isMarkedForDeletion,
          'text-red-400 hover:text-red-500': !isMarkedForDeletion,
        }"
        class="transition-colors duration-150"
      >
        <path d="M3 6h18" />
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
        <line x1="10" x2="10" y1="11" y2="17" />
        <line x1="14" x2="14" y1="11" y2="17" />
      </svg>
    </button>

    <!-- <button
      v-if="showPopoutButton"
      class="button transparent compact new-window--button"
      @click="popoutWindow"
    >
      <FluentIcon icon="open" size="22" class="text-n-slate-12" />
    </button>
    <button
      class="button transparent compact close-button"
      :class="{
        'rn-close-button': isRNWebView,
      }"
      @click="closeWindow"
    >
      <FluentIcon icon="dismiss" size="24" class="text-n-slate-12" />
    </button> -->

    <Modal v-model:show="showConfirmModal" :on-close="onCloseConfirm">
      <ModalHeader
        :header-title="$t('END_CONVERSATION')"
        :header-content="$t('CONVERSATION.END_CONFIRMATION')"
      />
      <div class="flex items-center justify-end gap-2 p-8">
        <button
          class="button secondary rounded-full shadow-lg"
          :style="{ borderColor: widgetColor }"
          @click="onCloseConfirm"
        >
          {{ $t('COMMON.CANCEL') }}
        </button>
        <button
          class="button danger rounded-full shadow-lg"
          :style="{ backgroundColor: widgetColor }"
          @click="onConfirmResolve"
        >
          {{ $t('COMMON.CONFIRM') }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.actions {
  .close-button {
    display: none;
  }

  .rn-close-button {
    display: block !important;
  }
}

.button {
  &.secondary {
    @apply bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 dark:bg-n-solid-3 dark:text-n-slate-12 dark:hover:bg-n-solid-2;
  }

  &.danger {
    @apply bg-n-ruby-9 text-white hover:bg-n-ruby-10 dark:bg-n-ruby-9 dark:text-white dark:hover:bg-n-ruby-10;
  }
}

/* 删除按钮样式 */
.delete-conversation-button {
  // 标记删除状态的样式
  &.bg-red-500 {
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
    opacity: 1 !important; // 确保标记状态下完全不透明

    &:hover {
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.5);
    }
  }
}
</style>
